import React from "react";
import { useTranslation } from "react-i18next";
import { CARD_PALETTE } from "./cardPalette";

interface ColorSwatchPickerProps {
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
}

/**
 * A fixed, curated grid of print-safe colors, deliberately not a free-form
 * `<input type="color">`. The whole point of this picker is that every
 * option already sits inside CMYK gamut (see cardPalette.ts), so letting a
 * customer pick an arbitrary RGB value would defeat that.
 */
const ColorSwatchPicker: React.FC<ColorSwatchPickerProps> = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup">
      {CARD_PALETTE.map(({ hex, nameKey }) => {
        const name = t(`cardDesign.colors.${nameKey}`);
        const selected = value.toUpperCase() === hex.toUpperCase();
        return (
          <button
            key={hex}
            type="button"
            role="radio"
            aria-checked={selected}
            title={name}
            aria-label={name}
            disabled={disabled}
            onClick={() => onChange(hex)}
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-transform disabled:opacity-50 disabled:cursor-not-allowed ${
              selected ? 'border-teal-600 scale-110' : 'border-gray-300 hover:scale-105'
            }`}
            style={{ backgroundColor: hex }}
          >
            {selected && (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke={hex === '#FFFFFF' || hex === '#E0E0E0' ? '#1A1A1A' : '#FFFFFF'}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ColorSwatchPicker;
